import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar, { FilterOptions } from './AnalyticsFilterBar';
import { rangeFor, toQuery, useAnalyticsFilters } from './filters';
import { DayTimeline, STATE_COLORS, TimeChart, TimelineSegment } from './charts';
import { fmtMin, fmtNum, fmtPct } from './format';
import { Kpi, Tile, alert, card, cardHead, grid, muted, td, tdR, th, thR } from './Overview';

// "Historia linii" (spec 7.7): one line — speed vs target, diameter X/Y in the tolerance band,
// day-by-day state timeline and the items that ran on it.

interface Point { t: string; speed: number | null; dx: number | null; dy: number | null; target: number | null; nominal: number }
interface TimelineRow extends TimelineSegment { start: string; end: string; speed: number | null; sku: string | null; material: string | null; color: string | null; target: number | null }
interface ItemRow { item_id: number; pivot_id: number | null; sku: string | null; name: string | null; material: string | null; color: string | null; brand: string | null; planned: number; done_total: number | null; status: string | null; week: string | null; started_at: string | null; completed_at: string | null; pcs: number; kgNet: number; kgFlow: number; prodMin: number; downMin: number; avgSpeed: number | null; performance: number | null }
interface Data {
  from: string; to: string; line: { id: number; name: string; external_code: string | null }; kpi: Kpi;
  telemetry: { bucket_seconds: number; points: Point[]; quality: { out_of_tolerance_pct: number | null; ovality_mm: number | null; samples: number } };
  timeline: Omit<TimelineRow, 'title'>[]; items: ItemRow[]; tolerance_mm: number;
}

const ts = (iso: string) => Math.floor(new Date(iso).getTime() / 1000);
const hhmm = (t: number) => { const d = new Date(t * 1000); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const ddmm = (t: number) => { const d = new Date(t * 1000); return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`; };
const dt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

const LineHistory: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [options, setOptions] = useState<FilterOptions | null>(null);
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.get('/analytics/filters').then(r => setOptions(r.data)).catch(() => null); }, []);

  // No line chosen yet -> the first line that has telemetry, so the screen is never empty on entry.
  useEffect(() => {
    if (!filters.lineId && options) {
      const first = options.lines.find(l => l.has_telemetry) ?? options.lines[0];
      if (first) setFilters({ lineId: String(first.id) });
    }
  }, [filters.lineId, options, setFilters]);

  useEffect(() => {
    if (!filters.lineId) return;
    let cancelled = false;
    setLoading(true);
    const { from, to, shift, material_id } = toQuery(filters);
    api.get(`/analytics/lines/${filters.lineId}/history`, { params: { from, to, shift, material_id } })
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, t]);

  const range = useMemo(() => { const r = rangeFor(filters); return { from: Math.floor(r.from.getTime() / 1000), to: Math.floor(Math.min(r.to.getTime(), Date.now()) / 1000) }; }, [filters]);
  const multiDay = range.to - range.from > 86400 * 1.5;
  const tick = multiDay ? ddmm : hhmm;

  const days = useMemo(() => {
    if (!data) return [];
    const out: { label: string; dayStart: number; segments: TimelineSegment[] }[] = [];
    const r = rangeFor(filters);
    for (let d = new Date(r.from); d < r.to && d.getTime() <= Date.now(); d.setDate(d.getDate() + 1)) {
      const dayStart = Math.floor(d.getTime() / 1000);
      const dayEnd = dayStart + 86400;
      const segments = data.timeline
        .filter(s => s.end_ts > dayStart && s.start_ts < dayEnd)
        .map(s => ({
          ...s,
          start_ts: Math.max(s.start_ts, dayStart),
          end_ts: Math.min(s.end_ts, dayEnd),
          title: `${hhmm(Math.max(s.start_ts, dayStart))}–${hhmm(Math.min(s.end_ts, dayEnd))} · ${t(`analytics.state_${s.state}`)}${s.reason ? ` (${t(`analytics.reason_${s.reason}`)})` : ''}${s.sku ? ` · ${s.sku}` : ''}${s.material ? ` · ${s.material}` : ''}${s.color ? ` · ${s.color}` : ''}${s.speed ? ` · ${fmtNum(s.speed, 1)} m/min` : ''}`,
        }));
      out.push({ label: ddmm(dayStart), dayStart, segments });
    }
    return out;
  }, [data, filters, t]);

  const k = data?.kpi;
  const pts = data?.telemetry.points ?? [];
  const nominal = pts.find(p => p.nominal)?.nominal ?? 1.75;
  const tol = data?.tolerance_mm ?? 0.03;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🧭 {t('lineHistory.title')}{data ? ` — ${data.line.name}` : ''}</h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('lineHistory.subtitle')}</p>
        </div>
      </div>

      <AnalyticsFilterBar filters={filters} onChange={setFilters} showCompare={false} />
      {error && <div style={alert}>{error}</div>}
      {!filters.lineId && <div style={{ color: '#94a3b8', padding: 32, textAlign: 'center' }}>{t('lineHistory.pickLine')}</div>}

      {k && data && (
        <div style={{ opacity: loading ? 0.6 : 1 }}>
          <div style={grid}>
            <Tile label={t('analytics.colProd')} value={fmtMin(k.prodMin)} sub={t('lineHistory.ofAvail', { pct: fmtPct(k.availability) })} delta={null} vsLabel="" bar={k.availability} />
            <Tile label={t('analytics.kpiAvgSpeed')} value={k.avgSpeed === null ? '—' : `${fmtNum(k.avgSpeed, 1)} m/min`} sub={k.tgtSpeed ? t('analytics.target', { value: fmtNum(k.tgtSpeed, 0) }) : t('analytics.noTargets')} delta={null} vsLabel="" />
            <Tile label="Performance" value={fmtPct(k.performance)} delta={null} vsLabel="" bar={k.performance} />
            <Tile label={t('analytics.kpiDowntime')} value={fmtMin(k.downMin)} sub={t('analytics.kpiDowntimeSub', { count: fmtNum(k.downCnt), avg: fmtMin(k.avgDownMin) })} delta={null} vsLabel="" />
            <Tile label={t('analytics.kpiShortStops')} value={String(k.ssCnt)} sub={t('analytics.kpiShortStopsSub', { count: fmtNum(k.ssCnt), rate: fmtNum(k.stopsPerProdH, 2) })} delta={null} vsLabel="" />
            <Tile label={t('lineHistory.outOfTol')} value={fmtPct(data.telemetry.quality.out_of_tolerance_pct)} sub={t('lineHistory.outOfTolSub', { tol: String(tol) })} delta={null} vsLabel="" />
            <Tile label={t('lineHistory.ovality')} value={data.telemetry.quality.ovality_mm === null ? '—' : `${fmtNum(data.telemetry.quality.ovality_mm, 3)} mm`} sub="|X − Y|" delta={null} vsLabel="" />
            <Tile label={t('analytics.kpiFlowKg')} value={`${fmtNum(k.kgFlow, 1)} kg`} sub={t('analytics.kpiFlowKgSub', { count: fmtNum(k.cycles) })} delta={null} vsLabel="" />
            <Tile label={t('analytics.kpiNoData')} value={fmtMin(k.noDataMin)} delta={null} vsLabel="" warn={k.noDataMin > k.availMin * 0.1} />
          </div>

          <div style={card}>
            <div style={cardHead}><b>{t('lineHistory.speedChart')}</b><span style={muted}>{t('lineHistory.bucket', { sec: String(data.telemetry.bucket_seconds) })}</span></div>
            {pts.length ? (
              <TimeChart
                from={range.from} to={range.to} tickLabel={tick} format={v => fmtNum(v, 0)} min={0}
                series={[
                  { name: t('lineHistory.speedActual'), color: '#2f80ed', points: pts.map(p => ({ t: ts(p.t), v: p.speed })) },
                  { name: 'Target', color: '#e8590c', dashed: true, points: pts.map(p => ({ t: ts(p.t), v: p.target })) },
                ]}
              />
            ) : <div style={{ ...muted, padding: 24, textAlign: 'center' }}>{t('lineHistory.noTelemetry')}</div>}
          </div>

          <div style={card}>
            <div style={cardHead}><b>{t('lineHistory.diameterChart')}</b><span style={muted}>Ø {nominal} ± {tol} mm</span></div>
            {pts.length ? (
              <TimeChart
                from={range.from} to={range.to} tickLabel={tick} format={v => v.toFixed(3)}
                min={nominal - tol * 4} max={nominal + tol * 4}
                band={{ from: nominal - tol, to: nominal + tol, color: 'rgba(15,157,88,0.12)' }}
                series={[
                  { name: 'Ø X', color: '#2f80ed', points: pts.map(p => ({ t: ts(p.t), v: p.dx })) },
                  { name: 'Ø Y', color: '#9333ea', points: pts.map(p => ({ t: ts(p.t), v: p.dy })) },
                ]}
              />
            ) : <div style={{ ...muted, padding: 24, textAlign: 'center' }}>{t('lineHistory.noTelemetry')}</div>}
          </div>

          <div style={card}>
            <div style={cardHead}>
              <b>{t('lineHistory.timeline')}</b>
              <span style={{ display: 'flex', gap: 14, fontSize: 12, color: '#475569' }}>
                {[['prod', t('analytics.state_prod')], ['down_short', t('analytics.stShort')], ['down', t('analytics.stDown')], ['nodata', t('analytics.state_nodata')]].map(([k2, label]) => (
                  <span key={k2} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: STATE_COLORS[k2] }} />{label}</span>
                ))}
              </span>
            </div>
            <DayTimeline days={days} shortStopSeconds={360} />
          </div>

          <div style={card}>
            <div style={cardHead}><b>{t('lineHistory.items')}</b><span style={muted}>{t('lineHistory.itemsSub')}</span></div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={th}>SKU</th><th style={th}>{t('lineHistory.colItem')}</th><th style={th}>{t('standards.colMaterial')}</th><th style={th}>{t('lineHistory.colColor')}</th>
                    <th style={thR}>{t('lineHistory.colPlan')}</th><th style={thR}>{t('lineHistory.colDoneTotal')}</th><th style={thR}>{t('lineHistory.colPcsInPeriod')}</th>
                    <th style={thR}>{t('analytics.colProd')}</th><th style={thR}>{t('analytics.colDown')}</th><th style={thR}>{t('analytics.colSpeed')}</th><th style={thR}>Performance</th>
                    <th style={th}>{t('lineHistory.colStarted')}</th><th style={th}>{t('lineHistory.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? <tr><td colSpan={13} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 24 }}>—</td></tr> : data.items.map(it => (
                    <tr key={`${it.item_id}-${it.pivot_id}`}>
                      <td style={{ ...td, fontWeight: 700 }}>{it.sku ?? '—'}</td>
                      <td style={{ ...td, whiteSpace: 'normal', minWidth: 200 }}>{it.name ?? '—'}<div style={muted}>{it.brand}{it.week ? ` · ${it.week}` : ''}</div></td>
                      <td style={td}>{it.material ?? '—'}</td>
                      <td style={td}>{it.color ?? '—'}</td>
                      <td style={tdR}>{it.planned || '—'}</td>
                      <td style={tdR}>{it.done_total ?? '—'}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{it.pcs}</td>
                      <td style={tdR}>{fmtMin(it.prodMin)}</td>
                      <td style={tdR}>{fmtMin(it.downMin)}</td>
                      <td style={tdR}>{fmtNum(it.avgSpeed, 1)}</td>
                      <td style={tdR}>{fmtPct(it.performance)}</td>
                      <td style={td}>{dt(it.started_at)}</td>
                      <td style={td}>{it.status ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineHistory;
